import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const deterministicSanitize = (content: string) => {
  if (!content) return "";

  return content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/\son\w+=\{[^}]*\}/gi, "")
    .replace(/\s(href|src)\s*=\s*(['"])javascript:[^'"]*\2/gi, ' $1="#"');
};

export const sanitizeHTML = (content: string) => {
  return {
    __html: deterministicSanitize(content || ""),
  };
};

export const shimmer = (w: number, h: number) => `
  <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <defs>
      <linearGradient id="g">
        <stop stop-color="#333" offset="20%" />
        <stop stop-color="#222" offset="50%" />
        <stop stop-color="#333" offset="70%" />
      </linearGradient>
    </defs>
    <rect width="${w}" height="${h}" fill="#333" />
    <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  </svg>`

export const toBase64 = (str: string) =>
  typeof window === 'undefined'
    ? Buffer.from(str).toString('base64')
    : window.btoa(str)

// Maximum concurrent requests to avoid overwhelming Vercel/Directus
export const MAX_CONCURRENT_REQUESTS = 3;

/**
 * Execute promises with a concurrency limit
 * @param items - Array of items to process
 * @param fn - Async function to execute for each item
 * @param limit - Maximum concurrent executions (default: MAX_CONCURRENT_REQUESTS)
 */
export async function promiseAllWithLimit<T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  limit: number = MAX_CONCURRENT_REQUESTS
): Promise<R[]> {
  const results: R[] = [];
  const executing: Promise<void>[] = [];

  for (const item of items) {
    const promise = fn(item).then((result) => {
      results.push(result);
    });

    executing.push(promise);

    if (executing.length >= limit) {
      await Promise.race(executing);
      // Remove completed promises
      const completed = executing.filter((p) => {
        let isResolved = false;
        p.then(() => (isResolved = true)).catch(() => (isResolved = true));
        return !isResolved;
      });
      executing.length = 0;
      executing.push(...completed);
    }
  }

  await Promise.all(executing);
  return results;
}

/**
 * Execute an array of promise-returning functions with a concurrency limit
 * @param fns - Array of functions that return promises
 * @param limit - Maximum concurrent executions
 */
export async function promisePoolAll<T>(
  fns: (() => Promise<T>)[],
  limit: number = MAX_CONCURRENT_REQUESTS
): Promise<T[]> {
  const results: T[] = new Array(fns.length);
  let currentIndex = 0;

  async function worker() {
    while (currentIndex < fns.length) {
      const index = currentIndex++;
      results[index] = await fns[index]();
    }
  }

  const workers = Array(Math.min(limit, fns.length))
    .fill(null)
    .map(() => worker());

  await Promise.all(workers);
  return results;
}
